import { css, ListItemText, Menu, MenuItem, styled } from "@mui/material";
import { Box, colors } from "@procore/core-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { Window } from "components";
import { ListNode } from "components/objectTree/types";
import { collapseAllNodes } from "components/objectTree/utils";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useDispatchHidden, useHidden } from "contexts/hidden";
import { highlightActions } from "contexts/highlighted";
import { useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { useDispatchSelectionBasket } from "contexts/selectionBasket";
import { BaseWindowProps } from "contexts/windows/reducer";
import { useFlyToSelected } from "features/flyToSelected/useFlyToSelected";
import { ModelTree } from "features/modelTree/components/modelTree";
import { emptyFileTypeFilters } from "features/modelTree/constants";
import { ModelTreeNodeData } from "features/modelTree/types";
import {
    renderActions,
    selectDefaultVisibility,
    selectMainObject,
    selectPreIsolationVisibility,
} from "features/render";
import { useAbortController } from "hooks/useAbortController";
import { useObjectTreeSearch } from "hooks/useObjectTreeSearch";
import { telemetry } from "telemetry";
import { selectObjectHierarchy, selectObjectHierarchyFromFilteredTree } from "utils/objectActions";
import { getGroupStateForFilteredTree, getGroupStateForIdSet } from "utils/treeHelpers";

import { ObjectTreeNodeProcore } from "./components/ObjectTreeNodeProcore";
import { ObjectTreeWindowHeader } from "./components/ObjectTreeWindowHeader";
import { ITEM_HEIGHT } from "./constants";
import { BulkAction, createBulkActions } from "./utils/bulkActions";

const ObjectTreeWindowContent = styled(Box)(
    () => css`
        height: 100%;
        overflow: hidden;

        .object-tree-scroll-container {
            scrollbar-gutter: stable;
            scrollbar-color: transparent transparent;

            &:hover {
                scrollbar-color: ${colors.gray70} transparent;
            }
        }
    `,
);

export function ObjectTreeWindow({ onClose, position = "center", ...props }: BaseWindowProps) {
    const { t } = useTranslation();
    const {
        state: { db },
    } = useExplorerGlobals();
    const dispatch = useAppDispatch();
    const mainObject = useAppSelector(selectMainObject);
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const preIsolationVisibility = useAppSelector(selectPreIsolationVisibility);
    const highlightedSet = useHighlighted();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHidden = useDispatchHidden();
    const hidden = useHidden();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const { flyToSelected } = useFlyToSelected();

    const {
        searchTerm,
        filteredObjectIds,
        tree,
        setTree,
        treeKey,
        isSearching,
        hasNoResults,
        searchError,
        handleSearchChange: handleSearchChangeBase,
        handleSearchSubmit,
    } = useObjectTreeSearch(db, mainObject);

    const handleSearchChange = useCallback(
        (value: string) => {
            if (value.length > 0) {
                dispatchHighlighted(highlightActions.setIds([]));
                dispatch(renderActions.setMainObject(undefined));
            }
            handleSearchChangeBase(value);
        },
        [dispatch, dispatchHighlighted, handleSearchChangeBase],
    );

    const [selectionAbortController] = useAbortController();

    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
        actions: BulkAction[];
        nodeId: number;
    } | null>(null);

    const closeContextMenu = useCallback(() => {
        setContextMenu(null);
    }, []);

    const onNodeRightClick = useCallback(
        (x: number, y: number, item: ListNode<ModelTreeNodeData>) => {
            const selected =
                filteredObjectIds != null
                    ? getGroupStateForFilteredTree(highlightedSet.ids, item, filteredObjectIds, tree)
                    : getGroupStateForIdSet(highlightedSet.ids, item, tree);

            const isAlreadySelected = selected !== "none";

            // If node is NOT already selected, clear and select it (async - will complete in background)
            if (!isAlreadySelected && db) {
                dispatchHighlighted(highlightActions.setIds([]));

                const objects = item.nodeGroup?.map((n) => n.data.object) ?? [item.node.data.object];

                // Note: dispatchHidden is intentionally NOT passed to selection functions.
                // Objects can exist in both selected and hidden sets simultaneously.
                const selectionPromise =
                    filteredObjectIds != null
                        ? selectObjectHierarchyFromFilteredTree({
                              nodes: objects,
                              filteredObjectIds,
                              dispatchHighlighted,
                              tree,
                          })
                        : selectObjectHierarchy({
                              nodes: objects,
                              db,
                              abortSignal: selectionAbortController.current.signal,
                              setLoading: () => {},
                              dispatchHighlighted,
                          });

                selectionPromise.catch((error) => {
                    if (!selectionAbortController.current.signal.aborted) {
                        console.error("Context menu selection failed:", error);
                        telemetry.trackCaughtException({
                            source: "objectTreeWindow.contextMenuSelection",
                            category: "data",
                            error,
                            severity: "error",
                        });
                    }
                });
            }

            const actions = createBulkActions(highlightedSet.idArr.length, highlightedSet.idArr, {
                t,
                dispatch,
                dispatchHighlighted,
                dispatchHidden,
                dispatchSelectionBasket,
                defaultVisibility,
                preIsolationVisibility,
                flyToSelected,
                currentMainObject: mainObject,
                onActionComplete: closeContextMenu,
                hiddenIds: hidden.ids,
                lastHiddenIds: hidden.lastHiddenIds,
            });
            setContextMenu({ mouseX: x, mouseY: y, actions, nodeId: item.node.data.object.id });
        },
        [
            db,
            filteredObjectIds,
            tree,
            highlightedSet.ids,
            highlightedSet.idArr,
            dispatchHighlighted,
            dispatchHidden,
            t,
            dispatch,
            dispatchSelectionBasket,
            defaultVisibility,
            preIsolationVisibility,
            flyToSelected,
            mainObject,
            closeContextMenu,
            selectionAbortController,
            hidden.ids,
            hidden.lastHiddenIds,
        ],
    );

    // Update context menu actions when selection changes while menu is open.
    // This handles the case where right-click triggers async selection that completes
    // after the menu is already displayed, ensuring menu items are enabled correctly.
    useEffect(() => {
        setContextMenu((openMenu) => {
            if (!openMenu) {
                return null;
            }

            const hasSelection = highlightedSet.idArr.length > 0;
            if (!hasSelection) {
                return openMenu;
            }

            const updatedActions = createBulkActions(highlightedSet.idArr.length, highlightedSet.idArr, {
                t,
                dispatch,
                dispatchHighlighted,
                dispatchHidden,
                dispatchSelectionBasket,
                defaultVisibility,
                preIsolationVisibility,
                flyToSelected,
                currentMainObject: mainObject,
                onActionComplete: closeContextMenu,
                hiddenIds: hidden.ids,
                lastHiddenIds: hidden.lastHiddenIds,
            });

            return { ...openMenu, actions: updatedActions };
        });
    }, [
        highlightedSet.idArr,
        t,
        dispatch,
        dispatchHighlighted,
        dispatchHidden,
        dispatchSelectionBasket,
        defaultVisibility,
        preIsolationVisibility,
        flyToSelected,
        mainObject,
        closeContextMenu,
        hidden.ids,
        hidden.lastHiddenIds,
    ]);

    const handleCollapseAll = useCallback(() => {
        setTree((currentTree) => collapseAllNodes(currentTree));
        void track(Events.objectTree.collapseAll);
    }, [setTree]);

    const emptyState = searchError
        ? {
              title: t("objectTreeSearchError"),
              message: searchError,
          }
        : hasNoResults
          ? {
                title: t("noResultsFound"),
                message: t("tryDifferentSearchTerm"),
            }
          : undefined;

    return (
        <Window
            position={position}
            size={{ width: 400, height: 600 }}
            loading={isSearching}
            loadingMessage={t("loadingResults")}
            emptyState={emptyState}
            header={
                <ObjectTreeWindowHeader
                    searchTerm={searchTerm}
                    onSearchChange={handleSearchChange}
                    onSearchSubmit={handleSearchSubmit}
                    onCollapseAll={handleCollapseAll}
                    tree={tree}
                    filteredObjectIds={filteredObjectIds}
                    hasNoResults={hasNoResults}
                    onClose={onClose}
                />
            }
            data-testid="model-tree-window"
            {...props}
        >
            <ObjectTreeWindowContent>
                <ModelTree
                    key={treeKey}
                    tree={tree}
                    setTree={setTree}
                    fileTypes={emptyFileTypeFilters}
                    NodeRenderer={ObjectTreeNodeProcore}
                    itemSize={ITEM_HEIGHT}
                    filteredObjectIds={filteredObjectIds}
                    searchTerm={searchTerm}
                    onNodeRightClick={onNodeRightClick}
                />
            </ObjectTreeWindowContent>

            <Menu
                open={contextMenu !== null}
                onClose={closeContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
                }
            >
                {contextMenu?.actions.map((action) => (
                    <MenuItem key={action.id} onClick={action.onClick} disabled={action.disabled}>
                        <ListItemText>{action.label}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </Window>
    );
}
